import { useCallback, useRef, useState } from 'react';
import HeroContent, { type HeroContentProps } from '../HeroContent';

interface HeroBannerProps {
  withVideo?: boolean;
  videoSrc?: string;
  imageSrc: string;
  videoFallbackImage?: string;
  content: HeroContentProps;
  scrollTarget?: string;
  desktopHeightClass?: string;
}

const HeroBanner = ({ withVideo = false, videoSrc, imageSrc,videoFallbackImage, content, scrollTarget, desktopHeightClass = "" }: HeroBannerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPaused(false);
      } else {
        videoRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  const handleArrowClick = useCallback(() => {
    if (scrollTarget) {
      const targetElement = document.getElementById(scrollTarget);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [scrollTarget]);

  return (
    <section
      className={`relative w-full ${desktopHeightClass ?? "lg:h-[100vh]"} flex items-center justify-center bg-center bg-cover
                        ${withVideo ? "lg:!bg-none pt-[6rem] lg:pt-0" : ""}
                        `}
      style={
        withVideo
          ? { backgroundImage: `url(${imageSrc})` }
          : {
            background: `url(${imageSrc}) no-repeat top / cover`,
            padding: "18.5rem 0",
            boxSizing: "border-box",
          }
      }
    >
      {/* purpose: Proper fallback on initial load. Graceful degradation when video isn’t supported or present. And, No flicker: The image is initially shown, and video fades in behind it when ready (thanks to z-index).  */}
      {withVideo && videoSrc && (
        //video is rendered only if there's a videoSrc
        <video
          ref={videoRef}
          className="hidden lg:block absolute top-1/2 left-1/2 w-full h-full 
            object-cover transform -translate-x-1/2 -translate-y-1/2 z-[-1]"
          autoPlay
          loop
          muted
          playsInline
          onCanPlayThrough={() => setVideoReady(true)}
          poster={videoFallbackImage ?? imageSrc}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}

      {withVideo && (!videoReady || !videoSrc) && (
        // image is rendered while the video is not ready(loading), or if there's no video at all (e.g. videoSrc undefined)
        <img
          src={videoFallbackImage ?? imageSrc}
          alt="Hero Banner Fallback"
          className={`
    hidden lg:block
    absolute top-0 left-0 w-full h-full object-cover z-0
    transition-opacity duration-300
    ${videoReady && !isPaused ? "opacity-0 pointer-events-none" : "opacity-100"}
  `}
          draggable={false}
        />
      )}

      <div className="absolute inset-0 bg-[rgba(0,0,0,.6)]" />

      {withVideo && (
        <button
          onClick={toggleVideo}
          className="hidden lg:block absolute bottom-5 right-5 bg-black/70 text-white border-none px-4 py-2 text-base rounded cursor-pointer z-20"
          aria-label={
            isPaused ? "Play background video" : "Pause background video"
          }
        >
          {isPaused ? "▶ Play" : "⏸ Pause"}
        </button>
      )}

      <HeroContent {...content} />

      <div className="pointer-events-none absolute bottom-0 left-1/2 w-[9.8125rem] h-[2.1875rem] bg-[url('/images/bg-hero-arrow.svg')] bg-no-repeat transform -translate-x-1/2 z-30">
        <img
          onClick={handleArrowClick}
          src="/images/icons/hero-down-arrow.svg"
          alt="Down Arrow"
          className="pointer-events-auto hover:border-[#d6d9e1] hover:rounded-full w-[3.5625rem] h-[3.5rem] absolute bottom-[-2.25rem] left-1/2 transform -translate-x-1/2 cursor-pointer border-[0.25rem] border-transparent transition-all duration-500 ease-in-out z-40"
        />
      </div>
    </section>
  );
};

export default HeroBanner;
